import { NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import {
  AngularFirestoreModule,
  SETTINGS as FIRESTORE_SETTINGS,
} from '@angular/fire/compat/firestore';
import {
  AngularFireFunctionsModule,
  REGION,
} from '@angular/fire/compat/functions';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import * as Sentry from '@sentry/angular-ivy';
import { NgChartsModule } from 'ng2-charts';
import { DragulaModule } from 'ng2-dragula';
import { AutosizeModule } from 'ngx-autosize';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import {
  arrow90DegUp,
  arrowCounterclockwise,
  arrowDown,
  arrowDownUp,
  arrowLeftRight,
  arrowLeftShort,
  arrowRight,
  arrowRightShort,
  arrowRightSquare,
  arrowsFullscreen,
  arrowUp,
  arrowUpRightSquare,
  asterisk,
  award,
  back,
  barChart,
  barChartFill,
  barChartLine,
  barChartLineFill,
  basket3Fill,
  batteryFull,
  bodyText,
  bookmarkFill,
  boxArrowDown,
  boxArrowInDown,
  boxArrowInLeft,
  boxArrowRight,
  boxArrowUp,
  bracesAsterisk,
  calculator,
  calculatorFill,
  calendar,
  calendar3,
  calendarDate,
  calendarEvent,
  calendarHeart,
  calendarPlus,
  cardText,
  caretRightSquare,
  chatFill,
  check,
  check2Square,
  checkAll,
  checkCircleFill,
  checkLg,
  checkSquare,
  checkSquareFill,
  chevronDown,
  chevronLeft,
  chevronRight,
  chevronUp,
  circleFill,
  clipboardFill,
  clipboardPlus,
  clock,
  clockFill,
  clockHistory,
  cloudArrowUpFill,
  cloudCheck,
  collection,
  creditCard,
  currencyEuro,
  dash,
  download,
  envelope,
  envelopeOpen,
  exclamation,
  exclamationCircleFill,
  exclamationDiamondFill,
  exclamationOctagonFill,
  eye,
  eyeFill,
  eyeSlash,
  eyeSlashFill,
  fileEarmark,
  fileEarmarkBreak,
  fileEarmarkCheck,
  fileEarmarkExcel,
  fileEarmarkPost,
  fileEarmarkPpt,
  fileLock,
  fileLockFill,
  filePdf,
  files,
  fileSpreadsheet,
  filetypeDoc,
  filter,
  flag,
  flagFill,
  flower2,
  flower3,
  gear,
  gearFill,
  genderAmbiguous,
  genderFemale,
  genderMale,
  graphUp,
  grid3X2GapFill,
  heartPulseFill,
  hypnotize,
  icon123,
  image,
  infoCircle,
  infoCircleFill,
  infoSquare,
  journalBookmarkFill,
  link45Deg,
  lock,
  lockFill,
  mic,
  micFill,
  NgxBootstrapIconsModule,
  paperclip,
  paragraph,
  pauseBtn,
  pauseFill,
  pen,
  pencil,
  pencilFill,
  pencilSquare,
  penFill,
  pentagon,
  people,
  peopleFill,
  person,
  personCircle,
  personDash,
  personFill,
  personPlus,
  phone,
  pieChart,
  pieChartFill,
  playBtnFill,
  playFill,
  plus,
  plusCircleDotted,
  plusCircleFill,
  plusLg,
  plusSquare,
  plusSquareDotted,
  plusSquareFill,
  printer,
  question,
  questionCircle,
  questionLg,
  questionSquare,
  recordBtn,
  recordCircle,
  robot,
  search,
  send,
  sliders,
  square,
  star,
  starFill,
  starHalf,
  stars,
  stopBtn,
  stopFill,
  textParagraph,
  threeDots,
  trash,
  trashFill,
  trophy,
  unlockFill,
  viewStacked,
  wallet2,
  x,
  xCircle,
  xCircleFill,
  xLg,
  xSquare,
  zoomIn,
} from 'ngx-bootstrap-icons';
import { NgxInnerLoadingModule } from 'ngx-inner-loading';
import { NgxPrinterModule } from 'ngx-printer';
import { ToastrModule } from 'ngx-toastr';
import { environment } from 'src/environments/environment';

import { AppComponent } from './app.component';
import { AppFacade } from './app.facade';
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { GlobalErrorHandler } from './core/error/global-error-handler';
import { AuthModule } from './data/auth/auth.module';
import { CompositionModule } from './data/composition/composition.module';
import { DietitianModule } from './data/dietitian/dietitian.module';
import { DocumentModule } from './data/document/document.module';
import { EmailModule } from './data/email/email.module';
import { HelpMeModule } from './data/help-me/help-me.module';
import { LoggerRepository } from './data/logger/repositories/logger_repository';
import { MessageModule } from './data/message/message.module';
import { MsdAiModule } from './data/msd-ai/msd-ai.module';
import { MsdpModule } from './data/msdp/msdp.module';
import { NewsModule } from './data/news/news.module';
import { NotificationModule } from './data/notification/notification.module';
import { PatientModule } from './data/patient/patient.module';
import { ResourceModule } from './data/resource/resource.module';
import { ToDoModule } from './data/todo/todo.module';
import { TranscriptionModule } from './data/transcription/transcription.module';
import { EInvoicingService } from './service/e-invoicing.service';
import { TasksService } from './service/tasks.service';
import { ToDoService } from './service/todo.service';
import { AuthenticationGuard } from './ui/authentication/authentication.guard';
import { EInvoicingBannerModule } from './ui/components/e-invoicing-banner/e-invoicing-banner.module';
import { NewsPopupModule } from './ui/components/news-popup/news-popup.module';
import { SubscriptionBannerModule } from './ui/components/subscription-banner/subscription-banner.module';
import DashboardGuard from './ui/dashboard/dashboard.guard';
import { DocumentGuard } from './ui/document/document.guard';
import { DocumentIdGuard } from './ui/document/document-id.guard';
import { EInvoicingGuard } from './ui/e-invoicing/e-invoicing.guard';
import { HelpMeUiModule } from './ui/help-me/help-me-ui.module';
import { MyGuard } from './ui/my/my.guard';
import { MyModule } from './ui/my/my.module';
import { NutritionGuard } from './ui/nutrition/nutrition.guard';
import { NutritionIdGuard } from './ui/nutrition/nutrition-id.guard';
import { PatientGuard } from './ui/patient/patient.guard';
import { TemplateGuard } from './ui/template/template.guard';
import { TemplateIdGuard } from './ui/template/template-id.guard';
import { TaskModalModule } from './ui/todo/components/task-modal/task-modal.module';
import { ToDoModalModule } from './ui/todo/components/todo-modal/todo-modal.module';

const icons = {
  icon123,
  boxArrowDown,
  boxArrowInDown,
  boxArrowUp,
  arrowDownUp,
  checkSquare,
  download,
  chevronLeft,
  creditCard,
  wallet2,
  chevronRight,
  plus,
  pauseFill,
  plusSquare,
  threeDots,
  trash,
  xSquare,
  phone,
  check,
  arrowLeftShort,
  search,
  arrowRightShort,
  pencil,
  pencilSquare,
  playFill,
  pen,
  questionSquare,
  questionCircle,
  send,
  x,
  eye,
  stopFill,
  envelopeOpen,
  check2Square,
  barChart,
  sliders,
  infoCircle,
  bodyText,
  calendar,
  calendarEvent,
  calendarDate,
  calendarHeart,
  graphUp,
  printer,
  trophy,
  personPlus,
  envelope,
  mic,
  lockFill,
  image,
  paperclip,
  fileEarmark,
  checkAll,
  cloudArrowUpFill,
  cloudCheck,
  plusCircleFill,
  boxArrowInLeft,
  unlockFill,
  asterisk,
  pentagon,
  gearFill,
  barChartLine,
  bracesAsterisk,
  xCircle,
  xCircleFill,
  filePdf,
  award,
  infoSquare,
  barChartLineFill,
  barChartFill,
  cardText,
  link45Deg,
  checkCircleFill,
  caretRightSquare,
  boxArrowRight,
  arrowUpRightSquare,
  arrow90DegUp,
  recordCircle,
  pencilFill,
  filetypeDoc,
  trashFill,
  genderMale,
  genderFemale,
  genderAmbiguous,
  question,
  questionLg,
  pieChart,
  flag,
  flagFill,
  pieChartFill,
  batteryFull,
  heartPulseFill,
  chevronDown,
  grid3X2GapFill,
  checkSquareFill,
  gear,
  square,
  star,
  starFill,
  filter,
  arrowUp,
  arrowDown,
  bookmarkFill,
  personCircle,
  infoCircleFill,
  currencyEuro,
  personDash,
  calendar3,
  fileEarmarkBreak,
  fileEarmarkCheck,
  clockFill,
  fileEarmarkPpt,
  fileEarmarkPost,
  journalBookmarkFill,
  starHalf,
  stars,
  fileSpreadsheet,
  exclamationCircleFill,
  arrowLeftRight,
  plusSquareDotted,
  plusSquareFill,
  arrowRight,
  dash,
  viewStacked,
  calculator,
  calculatorFill,
  exclamationDiamondFill,
  fileLock,
  fileLockFill,
  penFill,
  lock,
  fileEarmarkExcel,
  arrowRightSquare,
  chatFill,
  circleFill,
  flower3,
  flower2,
  hypnotize,
  robot,
  eyeFill,
  eyeSlashFill,
  playBtnFill,
  basket3Fill,
  files,
  back,
  plusCircleDotted,
  zoomIn,
  arrowsFullscreen,
  calendarPlus,
  chevronUp,
  clockHistory,
  collection,
  plusLg,
  clipboardPlus,
  person,
  people,
  checkLg,
  clock,
  xLg,
  exclamationOctagonFill,
  personFill,
  peopleFill,
  arrowCounterclockwise,
  clipboardFill,
  eyeSlash,
  exclamation,
  micFill,
  recordBtn,
  pauseBtn,
  stopBtn,
  paragraph,
  textParagraph,
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule,
    AngularFireStorageModule,
    AngularFireFunctionsModule,
    AngularFireMessagingModule,
    AppRoutingModule,
    BsDropdownModule.forRoot(),
    NgxBootstrapIconsModule.pick(icons),
    AuthModule.forRoot(),
    DietitianModule.forRoot(),
    MsdpModule.forRoot(),
    PatientModule.forRoot(),
    DocumentModule.forRoot(),
    CompositionModule.forRoot(),
    MessageModule.forRoot(),
    NotificationModule.forRoot(),
    EmailModule.forRoot(),
    ModalModule.forRoot(),
    PaginationModule.forRoot(),
    BsDatepickerModule.forRoot(),
    TimepickerModule.forRoot(),
    DragulaModule.forRoot(),
    CarouselModule.forRoot(),
    ToastrModule.forRoot({
      progressBar: true,
      positionClass: 'toast-bottom-right',
    }),
    CoreModule,
    CollapseModule.forRoot(),
    NgChartsModule.forRoot(),
    ProgressbarModule.forRoot(),
    AutosizeModule,
    NgxSliderModule,
    SubscriptionBannerModule,
    EInvoicingBannerModule,
    NgxPrinterModule.forRoot({ printOpenWindow: false }),
    FormsModule,
    HelpMeModule.forRoot(),
    NewsModule.forRoot(),
    MsdAiModule.forRoot(),
    ResourceModule.forRoot(),
    MyModule.forRoot(),
    NewsPopupModule,
    HelpMeUiModule,
    ToDoModule.forRoot(),
    TranscriptionModule.forRoot(),
    ToDoModalModule,
    TaskModalModule,
    NgxInnerLoadingModule,
  ],
  providers: [
    AppFacade,
    DashboardGuard,
    MyGuard,
    EInvoicingGuard,
    AuthenticationGuard,
    PatientGuard,
    TemplateGuard,
    TemplateIdGuard,
    NutritionGuard,
    NutritionIdGuard,
    DocumentGuard,
    DocumentIdGuard,
    TasksService,
    ToDoService,
    EInvoicingService,
    LoggerRepository,
    { provide: REGION, useValue: 'europe-west1' },
    {
      provide: FIRESTORE_SETTINGS,
      useValue: { experimentalForceLongPolling: true },
    },
    // ...(environment.production
    //   ? []
    //   : [
    //       {
    //         provide: USE_AUTH_EMULATOR,
    //         useValue: ['http://127.0.0.1:9099'],
    //       },
    //       {
    //         provide: USE_FIRESTORE_EMULATOR,
    //         useValue: ['127.0.0.1', 8080],
    //       },
    //       {
    //         provide: USE_FUNCTIONS_EMULATOR,
    //         useValue: ['127.0.0.1', 5001],
    //       },
    //       {
    //         provide: USE_STORAGE_EMULATOR,
    //         useValue: ['127.0.0.1', 9199],
    //       },
    //     ]),
    {
      provide: GlobalErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
